import React, { useCallback, useState, useRef, useMemo } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { Canvas3D } from './Canvas3D';
import { useEditorState } from './hooks/useEditorState';
import { useTools, TOOLS } from './hooks/useTools';
import { useInteraction } from './hooks/useInteraction';
import { useKeyboard } from './hooks/useKeyboard';
import { useCADSettings } from './hooks/useCADSettings';
import { useSnap } from './hooks/useSnap';
import { usePointSettings } from './hooks/usePointSettings';
import { useGeometryState } from './hooks/useGeometryState';
import { Grid3D } from './objects/Grid3D';
import { Point3D } from './objects/Point3D';
import { Wall3D } from './objects/Wall3D';
import { WallVolume3D } from './objects/WallVolume3D';
import { useWallState } from './hooks/building/useWallState';
import { useSpaceState } from './hooks/building/useSpaceState';
import { useMaterialBreaks } from './hooks/building/useMaterialBreaks';
import { useSlabState } from './hooks/building/useSlabState';
import { Space3D } from './objects/Space3D';
import { Shaft3D } from './objects/Shaft3D';
import { StubWall3D } from './objects/StubWall3D';
import { Solid3D, createSolid } from './objects/Solid3D';
import { SelectionBox3D } from './objects/SelectionBox3D';
import { CeilingView3D } from './objects/CeilingView3D';
import { ImageUnderlay3D } from './objects/ImageUnderlay3D';
import { MeasureOverlay3D } from './objects/MeasureOverlay3D';
import { SnapIndicator } from './objects/SnapIndicator';
import { ToolbarPanel } from './panels/ToolbarPanel';
import { PropertiesPanel } from './panels/PropertiesPanel';
import { SpaceListPanel } from './panels/SpaceListPanel';
import { StatusBar } from './panels/StatusBar';
import { QuantitiesPanel } from './panels/QuantitiesPanel';
import { SettingsPanel } from './panels/SettingsPanel';
import { MultiSelectionPanel } from './panels/MultiSelectionPanel';
import { UnderlayPanel } from './panels/UnderlayPanel';
import { MeasureResultDialog } from './panels/MeasureResultDialog';
import { DraggablePanel } from './panels/DraggablePanel';
import { MenuBar } from './panels/MenuBar';
import { LayerPanel } from './panels/LayerPanel';
import { uploadUnderlay, deleteUnderlay, export3dm, checkGeometryAvailable } from '../../api';
import { importOBJ, centerImport } from './utils/objImporter';
import { generateId, generateArcPoints, circleFrom3Points } from './utils/geometry';
import { SIZES, POINT_TYPES } from './utils/constants';
import { PendingLines, TrackingLine, CursorAnnotation, ArcPreview, ArcCenters, TransformPreview } from './components/scene';
import { useTransform, TRANSFORM_TYPES } from './hooks/tools/useTransform';
import { useDrawingTools } from './hooks/tools/useDrawingTools';
import { CurveObjects, GeometryPoints } from './objects/geometry';
import { DrawingPreview } from './components/DrawingPreview';
import { ControlPoints } from './components/ControlPoints';

export function StoreyEditor3D({
  storey,
  onUpdate,
  onClose,
  componentTypes = [],
}) {
  // Display Settings State - Defaults für große Grundrisse (Werte in mm)
  const DEFAULT_DISPLAY_SETTINGS = {
    viewMode: '2d',            // '2d' oder '3d'
    pointRadius: 100,          // mm in Weltkoordinaten
    wallWidth: 4,              // Pixel-Linienbreite
    showLabels: true,
    labelSize: 400,            // Raum-Schriftgröße: 400mm = 40cm
    showDimensions: true,
    dimensionFontSize: 250,    // Maß-Schriftgröße: 250mm = 25cm
    showGrid: true,
    gridOpacity: 0.2,
    showSpaceFill: true,
    spaceFillOpacity: 0.15,
    showWallOpenings: true,
    snapThreshold: 500,
    // 3D-Deckenansicht
    showLots: true,            // Lot-Linien (vertikal) anzeigen
    showCeilingPoints: true,   // Deckenpunkte anzeigen
    showCeilingLines: true,    // Deckenlinien anzeigen
    showCeilingFill: true,     // Deckenpolygone anzeigen
    showHeightLabels: true,    // Höhenbeschriftungen anzeigen
  };
  const [displaySettings, setDisplaySettings] = useState(DEFAULT_DISPLAY_SETTINGS);

  // Active Layer State (must be before useGeometryState)
  const [activeLayerId, setActiveLayerId] = useState('default');

  // Hooks
  const state = useEditorState(storey);
  const tools = useTools();
  const interaction = useInteraction();
  const cad = useCADSettings();
  const snap = useSnap(cad, displaySettings.snapThreshold);
  const pointSettings = usePointSettings();

  // Geometry State (Pure CAD - Rhino-like)
  const geometryState = useGeometryState(storey, { snapThreshold: displaySettings.snapThreshold, activeLayerId });

  // Wall Volume State (new 8-point wall model)
  const wallState = useWallState(geometryState);

  // Space/Room State (new wall-aware rooms)
  const spaceState = useSpaceState(wallState);

  // Material Breaks (2D zones on wall faces)
  const materialBreakState = useMaterialBreaks(wallState);

  // Floor Slabs (floor above / ceiling below)
  const slabState = useSlabState(geometryState);

  // Geometry snap data for point/midpoint snapping on geometry objects
  const geoSnap = useMemo(() => ({
    points: geometryState.points,
    curves: geometryState.curves,
  }), [geometryState.points, geometryState.curves]);

  // Drawing Tools (Rhino-style preview and dimension input)
  const drawingTools = useDrawingTools({
    activeTool: tools.activeTool,
    storeyHeight: state.storeyHeight,
  });

  // Transform-System (Move/Copy/Rotate/Scale/Mirror)
  const transform = useTransform({
    // Building point operations
    movePoint: state.movePoint,
    copyPoint: (x, y, z) => state.addPoint(x, y, z),  // Copy erstellt neuen Punkt
    getPoint: state.getPoint,
    selectedPointIds: interaction.selectedPointIds || [],
    // Geometry operations
    geometryMovePoint: geometryState.movePoint,
    geometryGetPoint: geometryState.getPoint,
    geometryTransformGeometry: geometryState.transformGeometry,
    selectedGeometryPointIds: interaction.selectedGeometryPointIds || [],
    selectedGeometryCurveIds: interaction.selectedGeometryCurveIds || [],
    selectedGeometrySolidIds: interaction.selectedGeometrySolidIds || [],
  });

  // Dragging State - deaktiviert MapControls während des Ziehens
  const [isDragging, setIsDragging] = useState(false);

  // Save-Feedback State
  const [saveMessage, setSaveMessage] = useState(null);

  // Underlay State (Grundriss-Unterlage)
  const [underlay, setUnderlay] = useState(storey?.underlay || null);
  const [isUploadingUnderlay, setIsUploadingUnderlay] = useState(false);

  // Measurement State (Messwerkzeug)
  const [measurePoints, setMeasurePoints] = useState([]);
  const [showMeasureDialog, setShowMeasureDialog] = useState(false);

  // Solid State (3D-Körper)
  const [selectedSolidId, setSelectedSolidId] = useState(null);
  const [hoveredSolidId, setHoveredSolidId] = useState(null);
  const [pendingBoxOrigin, setPendingBoxOrigin] = useState(null); // Origin für neuen Quader

  // Control Point State (for geometry editing)
  const [hoveredControlPoint, setHoveredControlPoint] = useState(null); // { objectId, pointIndex }

  // Measurement Handlers (müssen vor useKeyboard definiert werden!)
  const handleAddMeasurePoint = useCallback((x, y) => {
    setMeasurePoints(prev => [...prev, { x, y }]);
  }, []);

  const handleFinishMeasure = useCallback(() => {
    if (measurePoints.length >= 2) {
      setShowMeasureDialog(true);
    }
  }, [measurePoints]);

  const handleMeasureConvertToSpace = useCallback(() => {
    if (measurePoints.length >= 3) {
      // Punkte erstellen und Raum anlegen
      const pointIds = measurePoints.map(mp => {
        const zHeight = 0; // Rhino-style: Points at Z=0 in plan view
        const point = state.addPoint(mp.x, mp.y, zHeight);
        return point.id;
      });
      state.addSpace(pointIds);
    }
    setMeasurePoints([]);
    setShowMeasureDialog(false);
    tools.selectTool(TOOLS.SELECT);
  }, [measurePoints, state, pointSettings, tools]);

  const handleMeasureKeepPoints = useCallback(() => {
    // Punkte behalten, aber keinen Raum erstellen
    measurePoints.forEach(mp => {
      const zHeight = 0; // Rhino-style: Points at Z=0 in plan view
      state.addPoint(mp.x, mp.y, zHeight);
    });
    setMeasurePoints([]);
    setShowMeasureDialog(false);
    tools.selectTool(TOOLS.SELECT);
  }, [measurePoints, state, pointSettings, tools]);

  const handleMeasureDiscard = useCallback(() => {
    setMeasurePoints([]);
    setShowMeasureDialog(false);
    tools.selectTool(TOOLS.SELECT);
  }, [tools]);

  const handleClearMeasure = useCallback(() => {
    setMeasurePoints([]);
  }, []);

  // Polyline finisher (Enter-Taste)
  const handleFinishPolyline = useCallback(() => {
    const pending = tools.pendingPointIds;
    if (pending.length < 2) return; // Mindestens 2 Punkte für Polyline

    // Koordinaten aus den Pending-IDs extrahieren (Format: pl_x_y)
    const points = pending.map(id => {
      const parts = id.split('_');
      // Format: pl_1234_5678 → x=1234, y=5678
      if (parts.length >= 3) {
        return { x: parseFloat(parts[1]), y: parseFloat(parts[2]), z: 0 };
      }
      return null;
    }).filter(Boolean);

    if (points.length >= 2) {
      // Check if polyline is closed (last point close to first point)
      const first = points[0];
      const last = points[points.length - 1];
      const dist = Math.sqrt(Math.pow(last.x - first.x, 2) + Math.pow(last.y - first.y, 2));
      const isClosed = dist < 50; // 5cm threshold

      if (isClosed && points.length > 2) {
        // Remove last point (it's a duplicate of first) and close the polyline
        points.pop();
        // Create closed polyline (flat 2D solid in XY plane)
        geometryState.addPolylineWithPoints(points, true);
      } else {
        geometryState.addPolylineWithPoints(points, false); // open polyline
      }
    }

    tools.resetPending();
  }, [tools.pendingPointIds, geometryState, tools, state.storeyHeight]);

  // Keyboard Shortcuts (erweitert für CAD-Modi)
  useKeyboard({
    selectTool: tools.selectTool,
    clearSelection: interaction.clearSelection,
    resetPending: () => {
      tools.resetPending();
      // Bei ESC im Measure-Tool: Messung zurücksetzen
      if (tools.activeTool === TOOLS.MEASURE && measurePoints.length > 0) {
        setMeasurePoints([]);
      }
    },
    resetDrawingTools: drawingTools.reset,
    finishMeasure: handleFinishMeasure,
    finishPolyline: handleFinishPolyline,
    activeTool: tools.activeTool,
    toggleOrtho: cad.toggleOrtho,
    toggleGridSnap: cad.toggleGridSnap,
    toggleOsnap: cad.toggleOsnap,
    deleteSelectedPoint: () => {
      if (interaction.selectedPointId) {
        state.deletePoint(interaction.selectedPointId);
        interaction.clearSelection();
      }
    },
    deleteSelectedWall: () => {
      if (interaction.selectedWallId) {
        state.deleteWall(interaction.selectedWallId);
        interaction.clearSelection();
      }
    },
    deleteSelectedSpace: () => {
      if (interaction.selectedSpaceId) {
        state.deleteSpace(interaction.selectedSpaceId);
        interaction.clearSelection();
      }
    },
    deleteSelectedGeometry: () => {
      const ids = [
        ...(interaction.selectedGeometryPointIds || []),
        ...(interaction.selectedGeometryCurveIds || []),
        ...(interaction.selectedGeometrySolidIds || []),
      ];
      if (ids.length > 0) {
        geometryState.deleteGeometries(ids);
        interaction.clearSelection();
        return true;
      }
      return false;
    },
    explodeSelectedArc: () => {
      // Prüfen ob ausgewählter Punkt zu einem Arc gehört
      if (interaction.selectedPointId) {
        const arc = state.getArcForPoint(interaction.selectedPointId);
        if (arc) {
          state.explodeArc(arc.id);
          interaction.clearSelection();
        }
      }
    },
    selectedPointId: interaction.selectedPointId,
    selectedWallId: interaction.selectedWallId,
    selectedSpaceId: interaction.selectedSpaceId,
    undo: state.undo,
    redo: state.redo,
    // Transform-Funktionen
    startMove: transform.startMove,
    startCopy: transform.startCopy,
    cancelTransform: transform.cancelTransform,
    isTransformActive: transform.isActive,
    // Wall Volume: W key creates wall from selected curve(s)
    createWallFromSelectedCurves: () => {
      const curveIds = interaction.selectedGeometryCurveIds || [];
      if (curveIds.length === 0) return false;
      curveIds.forEach(curveId => {
        wallState.createWallFromCurve(curveId, {
          thickness: 240,
          height: state.storeyHeight || 2750,
        });
      });
      return true;
    },
    flipSelectedWall: () => {
      // Flip wall inner/outer for selected wallState walls
      const wallIds = interaction.selectedWallIds || [];
      wallIds.forEach(id => {
        if (wallState.walls.find(w => w.id === id)) {
          wallState.flipWall(id);
        }
      });
    },
  });

  // OBJ Import Handler
  const handleImportOBJ = useCallback(async (file) => {
    try {
      const { points, walls } = await importOBJ(file);
      const centered = centerImport(points, walls);
      state.importData(centered);
    } catch (error) {
      console.error('OBJ Import fehlgeschlagen:', error);
      alert('OBJ Import fehlgeschlagen: ' + error.message);
    }
  }, [state]);

  // Underlay Upload Handler
  const handleUnderlayUpload = useCallback(async (file, page = 0) => {
    setIsUploadingUnderlay(true);
    try {
      const result = await uploadUnderlay(file, page);
      if (result.success) {
        setUnderlay({
          underlayId: result.underlayId,
          imageUrl: result.imageUrl,
          width: result.width,
          height: result.height,
          pageCount: result.pageCount,
          currentPage: result.currentPage,
          visible: true,
          opacity: 0.5,
          scale: 1,
          rotation: 0,
          position: { x: 0, y: 0 },
        });
      } else {
        alert('Underlay Upload fehlgeschlagen: ' + (result.message || 'Unbekannter Fehler'));
      }
    } catch (error) {
      console.error('Underlay Upload fehlgeschlagen:', error);
      alert('Underlay Upload fehlgeschlagen: ' + error.message);
    }
    setIsUploadingUnderlay(false);
  }, []);

  // Underlay Delete Handler
  const handleUnderlayDelete = useCallback(async () => {
    if (underlay?.underlayId) {
      await deleteUnderlay(underlay.underlayId);
    }
    setUnderlay(null);
  }, [underlay]);

  // Speichern - Geometrie-Daten mit bestehenden Komponenten zusammenführen
  const handleSave = useCallback(() => {
    const data = state.exportData;

    // Spaces zusammenführen: Geometrie-Daten behalten, aber components aus Original übernehmen
    const mergedSpaces = data.spaces.map(newSpace => {
      const originalSpace = storey?.spaces?.find(s => s.id === newSpace.id);
      return {
        ...newSpace,
        // Bestehende Komponenten beibehalten
        components: originalSpace?.components || [],
      };
    });

    onUpdate?.({
      ...storey,
      points: data.points,
      walls: data.walls,
      spaces: mergedSpaces,
      underlay: underlay, // Underlay-Einstellungen mitspeichern
    });

    // Feedback anzeigen
    setSaveMessage('Gespeichert!');
    setTimeout(() => setSaveMessage(null), 2000);
  }, [storey, state.exportData, onUpdate, underlay]);

  // 3DM Export (Rhino-Format)
  const handleExport3dm = useCallback(async () => {
    const data = state.exportData;

    setSaveMessage('Exportiere 3DM...');

    const result = await export3dm({
      points: data.points,
      walls: data.walls,
      spaces: data.spaces,
      storeyHeight: state.storeyHeight,
    });

    if (result.success) {
      setSaveMessage('3DM exportiert!');
    } else {
      setSaveMessage('Export fehlgeschlagen');
    }
    setTimeout(() => setSaveMessage(null), 3000);
  }, [state.exportData, state.storeyHeight]);

  // Reset
  const handleReset = useCallback(() => {
    if (confirm('Alle Änderungen verwerfen?')) {
      state.reset();
      interaction.clearSelection();
      tools.resetPending();
    }
  }, [state, interaction, tools]);

  // Canvas-Klick (nichts getroffen)
  const handleCanvasMiss = useCallback(() => {
    if (tools.activeTool === TOOLS.SELECT && !interaction.selectionBox) {
      interaction.clearSelection();
    }
  }, [tools.activeTool, interaction]);

  // Box-Selection Handler
  const handleBoxSelectionStart = useCallback((x, y) => {
    interaction.startBoxSelection(x, y);
  }, [interaction]);

  const handleBoxSelectionUpdate = useCallback((x, y) => {
    interaction.updateBoxSelection(x, y);
  }, [interaction]);

  const handleBoxSelectionFinish = useCallback(() => {
    interaction.finishBoxSelection(
      state.points,
      state.walls,
      state.spaces,
      state.shafts,
      state.stubWalls
    );
  }, [interaction, state]);

  // Koordinaten-Eingabe von StatusBar
  const handleCoordinateInput = useCallback(({ x, y }) => {
    if (tools.activeTool === TOOLS.POINT && x != null && y != null) {
      const existing = state.findNearestPoint(x, y);
      if (!existing) {
        // Z-Höhe aus Punkt-Einstellungen verwenden
        const zHeight = 0; // Rhino-style: Points at Z=0 in plan view
        state.addPoint(x, y, zHeight);
        cad.setLastPoint({ x, y });
      }
    }
  }, [tools.activeTool, state, cad, snap, pointSettings, geoSnap]);

  // Distanz-Eingabe von StatusBar (Punkt in Richtung der Maus setzen)
  const handleDistanceInput = useCallback(({ x, y }) => {
    if (x == null || y == null) return;

    if (tools.activeTool === TOOLS.POINT) {
      // Grid-Snap anwenden
      const snapped = snap.getSnappedPosition(x, y, state.points, state.walls, geoSnap);
      const existing = state.findNearestPoint(snapped.x, snapped.y);
      if (!existing) {
        // Z-Höhe aus Punkt-Einstellungen verwenden
        const zHeight = 0; // Rhino-style: Points at Z=0 in plan view
        state.addPoint(snapped.x, snapped.y, zHeight);
        cad.setLastPoint({ x: snapped.x, y: snapped.y });
      }
    } else if (tools.activeTool === TOOLS.WALL || tools.activeTool === TOOLS.SPACE || tools.activeTool === TOOLS.SHAFT || tools.activeTool === TOOLS.STUB_WALL) {
      // Bei Wand/Raum/Schacht/Vorwand-Tool: Punkt erstellen und zu pending hinzufügen
      const snapped = snap.getSnappedPosition(x, y, state.points, state.walls, geoSnap);
      // Bestimme Punkttyp: Schacht/Vorwand → auxiliary, Wand/Raum → wall
      const isAuxiliaryTool = tools.activeTool === TOOLS.SHAFT || tools.activeTool === TOOLS.STUB_WALL;
      const pointType = isAuxiliaryTool ? POINT_TYPES.AUXILIARY : POINT_TYPES.WALL;

      // Z-Höhe aus Punkt-Einstellungen verwenden
      const zHeight = 0; // Rhino-style: Points at Z=0 in plan view

      let point = state.findNearestPoint(snapped.x, snapped.y);
      if (!point) {
        point = state.addPoint(snapped.x, snapped.y, zHeight, pointType);
      }
      if (point) {
        tools.addPendingPoint(point.id);
        cad.setLastPoint({ x: snapped.x, y: snapped.y });
      }
    }
  }, [tools, state, cad, snap, pointSettings, geoSnap]);

  // Dimension-Eingabe für Drawing Tools (Circle radius, Rectangle size, etc.)
  const handleDrawingDimensionInput = useCallback((input) => {
    if (!drawingTools.isWaitingForSecondPoint) return;

    const result = drawingTools.confirmWithDimension(input);
    if (!result) return;

    // Create geometry based on tool type
    const toolKey = tools.activeTool?.toLowerCase?.();

    if (toolKey === 'circle' && result.preview) {
      geometryState.addCircleWithPoints(result.preview.center, result.preview.radius);
      tools.selectTool(TOOLS.SELECT);
    } else if (toolKey === 'rectangle' && result.firstPoint && result.secondPoint) {
      geometryState.addRectangle(result.firstPoint, result.secondPoint);
      tools.selectTool(TOOLS.SELECT);
    } else if (toolKey === 'polygon' && result.preview) {
      geometryState.addPolygon(
        result.preview.center,
        result.preview.radius,
        result.preview.sides,
        result.preview.rotation
      );
      tools.selectTool(TOOLS.SELECT);
    } else if (toolKey === 'line' && result.firstPoint && result.secondPoint) {
      geometryState.addLineWithPoints(result.firstPoint, result.secondPoint);
      cad.setLastPoint({ x: result.secondPoint.x, y: result.secondPoint.y });
    } else if (toolKey === 'construction_line' && result.firstPoint && result.secondPoint) {
      geometryState.addConstructionLine(result.firstPoint, result.secondPoint);
      cad.setLastPoint({ x: result.secondPoint.x, y: result.secondPoint.y });
    } else if (toolKey === 'cylinder' && result.preview) {
      geometryState.addCylinder(
        { x: result.preview.center.x, y: result.preview.center.y, z: 0 },
        result.preview.radius,
        result.preview.height
      );
      tools.selectTool(TOOLS.SELECT);
    } else if (toolKey === 'sphere' && result.preview) {
      geometryState.addSphere(result.preview.center, result.preview.radius);
      tools.selectTool(TOOLS.SELECT);
    } else if (toolKey === 'box' && result.preview) {
      const c1 = result.preview.corner1;
      const c2 = result.preview.corner2;
      const width = Math.abs(c2.x - c1.x);
      const depth = Math.abs(c2.y - c1.y);
      const centerX = (c1.x + c2.x) / 2;
      const centerY = (c1.y + c2.y) / 2;
      geometryState.addBox(
        { x: centerX, y: centerY, z: result.preview.height / 2 },
        width,
        depth,
        result.preview.height
      );
      tools.selectTool(TOOLS.SELECT);
    }
  }, [drawingTools, tools, geometryState, cad]);

  // Ausgewählte Objekte für PropertiesPanel
  const selectedPoint = interaction.selectedPointId
    ? state.getPoint(interaction.selectedPointId)
    : null;

  const selectedWall = interaction.selectedWallId
    ? state.walls.find(w => w.id === interaction.selectedWallId)
    : null;

  const selectedSpace = interaction.selectedSpaceId
    ? state.spaces.find(s => s.id === interaction.selectedSpaceId)
    : null;

  const selectedWallStart = selectedWall
    ? state.getPoint(selectedWall.startPointId)
    : null;

  const selectedWallEnd = selectedWall
    ? state.getPoint(selectedWall.endPointId)
    : null;

  const selectedSpacePolygon = selectedSpace
    ? state.getSpacePolygon(selectedSpace)
    : null;

  // Menu Action Handler
  const handleMenuAction = useCallback((action) => {
    switch (action) {
      case 'save':
        handleSave();
        break;
      case 'export3dm':
        handleExport3dm();
        break;
      case 'importObj':
        // Trigger file input
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.obj';
        input.onchange = (e) => {
          const file = e.target.files?.[0];
          if (file) handleImportOBJ(file);
        };
        input.click();
        break;
      case 'reset':
        handleReset();
        break;
      case 'delete':
        if (interaction.selectedPointId) {
          state.deletePoint(interaction.selectedPointId);
          interaction.clearSelection();
        }
        break;
      case 'selectAll':
        // Select all points
        interaction.setSelectedPointIds(state.points.map(p => p.id));
        break;
      case 'deselectAll':
        interaction.clearSelection();
        break;
      case 'view2d':
        setDisplaySettings(prev => ({ ...prev, viewMode: '2d' }));
        break;
      case 'view3d':
        setDisplaySettings(prev => ({ ...prev, viewMode: '3d' }));
        break;
      case 'toggleGrid':
        setDisplaySettings(prev => ({ ...prev, showGrid: !prev.showGrid }));
        break;
      case 'transform.move':
        transform.startMove();
        break;
      case 'transform.copy':
        transform.startCopy();
        break;
      case 'transform.rotate':
        transform.startRotate();
        break;
      case 'transform.scale':
        transform.startScale();
        break;
      case 'transform.mirror':
        transform.startMirror();
        break;
      default:
        console.log('Menu action:', action);
    }
  }, [handleSave, handleExport3dm, handleImportOBJ, handleReset, interaction, state, transform]);

  return (
    <div className="relative w-full h-full bg-slate-100 flex flex-col">
      {/* Menu Bar - oben */}
      <MenuBar
        onSelectTool={tools.selectTool}
        onAction={handleMenuAction}
        activeTool={tools.activeTool}
      />

      {/* Main Content */}
      <div className="flex-1 relative">
      {/* 3D Canvas */}
      <Canvas3D
        onPointerMissed={handleCanvasMiss}
        isDragging={isDragging}
        viewMode={displaySettings.viewMode}
      >
        {/* Grid */}
        {displaySettings.showGrid && (
          <Grid3D opacity={displaySettings.gridOpacity} viewMode={displaySettings.viewMode} />
        )}

        {/* Box Selection (nur im Select-Tool und 2D-Modus) */}
        <SelectionBox3D
          selectionBox={interaction.selectionBox}
          onStartSelection={handleBoxSelectionStart}
          onUpdateSelection={handleBoxSelectionUpdate}
          onFinishSelection={handleBoxSelectionFinish}
          enabled={tools.activeTool === TOOLS.SELECT && displaySettings.viewMode === '2d'}
        />

        {/* Scene Content */}
        <SceneContent
          state={state}
          tools={tools}
          interaction={interaction}
          setIsDragging={setIsDragging}
          cad={cad}
          snap={snap}
          displaySettings={displaySettings}
          pointSettings={pointSettings}
          underlay={underlay}
          onUnderlayDrag={(newPos) => setUnderlay(prev => ({ ...prev, position: newPos }))}
          measurePoints={measurePoints}
          onAddMeasurePoint={handleAddMeasurePoint}
          componentTypes={componentTypes}
          selectedSolidId={selectedSolidId}
          setSelectedSolidId={setSelectedSolidId}
          hoveredSolidId={hoveredSolidId}
          setHoveredSolidId={setHoveredSolidId}
          transform={transform}
          geometryState={geometryState}
          drawingTools={drawingTools}
          geoSnap={geoSnap}
          hoveredControlPoint={hoveredControlPoint}
          setHoveredControlPoint={setHoveredControlPoint}
          wallState={wallState}
        />
      </Canvas3D>

      {/* UI Panels - alle verschiebbar */}

      {/* Toolbar - links oben */}
      <DraggablePanel
        id="toolbar"
        title="Werkzeuge"
        defaultPosition={{ x: 16, y: 60 }}
        width="w-auto"
        zIndex={20}
      >
        <ToolbarPanel
          activeTool={tools.activeTool}
          selectTool={tools.selectTool}
          onImportOBJ={handleImportOBJ}
          onSave={handleSave}
          onExport3dm={handleExport3dm}
          onReset={handleReset}
          pendingCount={tools.pendingPointIds.length}
          embedded={true}
        />
      </DraggablePanel>

      {/* Eigenschaften - rechts oben (nur wenn Auswahl) */}
      {(selectedPoint || selectedWall || selectedSpace) && (
        <DraggablePanel
          id="properties"
          title={selectedPoint ? 'Punkt' : selectedWall ? 'Wand' : 'Raum'}
          defaultPosition={{ x: window.innerWidth - 300, y: 60 }}
          width="w-72"
          zIndex={30}
          onClose={interaction.clearSelection}
        >
          <PropertiesPanel
            selectedPoint={selectedPoint}
            selectedWall={selectedWall}
            selectedSpace={selectedSpace}
            startPoint={selectedWallStart}
            endPoint={selectedWallEnd}
            spacePolygon={selectedSpacePolygon}
            componentTypes={componentTypes}
            onUpdatePoint={(id, updates) => state.updatePoint(id, updates)}
            onUpdateWall={(id, updates) => state.updateWall(id, updates)}
            onUpdateSpace={(id, updates) => state.updateSpace(id, updates)}
            onDeletePoint={(id) => {
              state.deletePoint(id);
              interaction.clearSelection();
            }}
            onDeleteWall={(id) => {
              state.deleteWall(id);
              interaction.clearSelection();
            }}
            onDeleteSpace={(id) => {
              state.deleteSpace(id);
              interaction.clearSelection();
            }}
            onAddOpening={(wallId, openingDefaults = {}) => {
              state.addOpeningToWall(wallId, openingDefaults);
            }}
            onUpdateOpening={(wallId, openingId, updates) => {
              state.updateOpening(wallId, openingId, updates);
            }}
            onDeleteOpening={(wallId, openingId) => {
              state.deleteOpening(wallId, openingId);
            }}
            onDivideWall={(wallId, options) => {
              state.divideWall(wallId, options);
              interaction.clearSelection();
            }}
            onClose={interaction.clearSelection}
            embedded={true}
            // Arc-Eigenschaften
            arcs={state.arcs}
            getArcForPoint={state.getArcForPoint}
            onUpdateArcSegments={state.updateArcSegments}
            onDeleteArc={(arcId) => {
              state.deleteArc(arcId);
              interaction.clearSelection();
            }}
            onExplodeArc={(arcId) => {
              state.explodeArc(arcId);
              interaction.clearSelection();
            }}
          />
        </DraggablePanel>
      )}

      {/* Raumliste - links unten */}
      <DraggablePanel
        id="spacelist"
        title="Räume"
        defaultPosition={{ x: 16, y: 300 }}
        width="w-56"
        zIndex={15}
      >
        <SpaceListPanel
          spaces={state.spaces}
          points={state.points}
          selectedSpaceId={interaction.selectedSpaceId}
          onSelectSpace={interaction.selectSpace}
          embedded={true}
        />
      </DraggablePanel>

      {/* Mengenermittlung - rechts unten */}
      <DraggablePanel
        id="quantities"
        title="Mengen"
        defaultPosition={{ x: window.innerWidth - 340, y: 350 }}
        width="w-80"
        zIndex={15}
      >
        <QuantitiesPanel
          quantities={state.quantities}
          componentTypes={componentTypes}
          walls={state.exportData.walls}
          spaces={state.exportData.spaces}
          onSelectWall={interaction.selectWall}
          onSelectSpace={interaction.selectSpace}
          embedded={true}
        />
      </DraggablePanel>

      {/* Einstellungen - links mitte */}
      <DraggablePanel
        id="settings"
        title="Anzeige"
        defaultPosition={{ x: 16, y: 500 }}
        width="w-64"
        zIndex={15}
        defaultMinimized={true}
      >
        <SettingsPanel
          settings={displaySettings}
          onUpdateSettings={setDisplaySettings}
          embedded={true}
        />
      </DraggablePanel>

      {/* Grundriss-Unterlage - links unten */}
      <DraggablePanel
        id="underlay"
        title="Grundriss-Unterlage"
        defaultPosition={{ x: 16, y: 700 }}
        width="w-64"
        zIndex={15}
        defaultMinimized={true}
      >
        <UnderlayPanel
          underlay={underlay}
          onUpdateUnderlay={setUnderlay}
          onUpload={handleUnderlayUpload}
          onDelete={handleUnderlayDelete}
          isUploading={isUploadingUnderlay}
          embedded={true}
        />
      </DraggablePanel>

      {/* Multi-Selection Panel - mitte oben (nur wenn Mehrfachauswahl) */}
      {(interaction.selectedPointIds?.length > 1 || interaction.selectedWallIds?.length > 1 || interaction.selectedSpaceIds?.length > 1) && (
        <DraggablePanel
          id="multiselect"
          title="Mehrfachauswahl"
          defaultPosition={{ x: window.innerWidth / 2 - 150, y: 60 }}
          width="w-72"
          zIndex={35}
          onClose={interaction.clearSelection}
        >
          <MultiSelectionPanel
            selectedPointIds={interaction.selectedPointIds}
            selectedWallIds={interaction.selectedWallIds}
            selectedSpaceIds={interaction.selectedSpaceIds}
            points={state.points}
            walls={state.walls}
            spaces={state.spaces}
            componentTypes={componentTypes}
            onUpdatePoints={(id, updates) => state.updatePoint(id, updates)}
            onUpdateWalls={(id, updates) => state.updateWall(id, updates)}
            onUpdateSpaces={(id, updates) => state.updateSpace(id, updates)}
            onDeletePoints={(id) => state.deletePoint(id)}
            onDeleteWalls={(id) => state.deleteWall(id)}
            onDeleteSpaces={(id) => state.deleteSpace(id)}
            onMergeWalls={(id1, id2) => state.mergeWalls(id1, id2)}
            onClearSelection={interaction.clearSelection}
            embedded={true}
          />
        </DraggablePanel>
      )}

      {/* Layer Panel - rechts */}
      <DraggablePanel
        id="layers"
        title="Layer"
        defaultPosition={{ x: window.innerWidth - 220, y: 300 }}
        width="w-52"
        zIndex={25}
      >
        <LayerPanel
          layers={geometryState.layers}
          activeLayerId={activeLayerId}
          onLayerAdd={geometryState.addLayer}
          onLayerDelete={geometryState.deleteLayer}
          onLayerUpdate={geometryState.updateLayer}
          onLayerSelect={setActiveLayerId}
          counts={geometryState.layerCounts}
          selectedIds={[
            ...(interaction.selectedGeometryPointIds || []),
            ...(interaction.selectedGeometryCurveIds || []),
            ...(interaction.selectedGeometrySolidIds || []),
          ]}
          onMoveToLayer={geometryState.setGeometryLayer}
        />
      </DraggablePanel>

      </div>{/* End of flex-1 container */}

      {/* StatusBar (unten) */}
      <StatusBar
        mousePosition={cad.mousePosition}
        lastPoint={cad.lastPoint}
        orthoMode={cad.orthoMode}
        gridSnap={cad.gridSnap}
        gridSize={cad.gridSize}
        osnap={cad.osnap}
        activeTool={tools.activeTool}
        onToggleOrtho={cad.toggleOrtho}
        onToggleGridSnap={cad.toggleGridSnap}
        onToggleOsnap={cad.toggleOsnap}
        onGridSizeChange={cad.setGridSize}
        onCoordinateInput={handleCoordinateInput}
        onDistanceInput={handleDistanceInput}
        pointSettings={pointSettings}
        arcSegments={tools.arcSegments}
        onArcSegmentsChange={tools.setArcSegments}
        drawingTools={drawingTools}
        onDrawingDimensionInput={handleDrawingDimensionInput}
      />

      {/* Header mit Schließen-Button */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white rounded-lg shadow px-4 py-2 flex items-center gap-4">
        <span className="font-medium text-gray-800">
          {storey?.name || 'Geschoss bearbeiten'}
        </span>
        <button
          onClick={onClose}
          className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm"
        >
          Schließen
        </button>
      </div>

      {/* Save-Feedback Toast */}
      {saveMessage && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg animate-pulse z-50">
          {saveMessage}
        </div>
      )}

      {/* Measure Tool Info Bar */}
      {tools.activeTool === TOOLS.MEASURE && measurePoints.length > 0 && !showMeasureDialog && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-4">
          <span className="font-medium">{measurePoints.length} Punkt{measurePoints.length !== 1 ? 'e' : ''}</span>
          <button
            onClick={handleFinishMeasure}
            className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm"
          >
            Fertig (Enter)
          </button>
          <button
            onClick={handleClearMeasure}
            className="px-3 py-1 bg-white/10 hover:bg-red-500/50 rounded text-sm"
          >
            Abbrechen (Esc)
          </button>
        </div>
      )}

      {/* Measure Result Dialog */}
      {showMeasureDialog && (
        <MeasureResultDialog
          measurePoints={measurePoints}
          onConvertToSpace={handleMeasureConvertToSpace}
          onKeepPoints={handleMeasureKeepPoints}
          onDiscard={handleMeasureDiscard}
          onClose={() => setShowMeasureDialog(false)}
        />
      )}

      {/* Transform Info Bar */}
      {transform.isActive && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-4">
          <span className="font-medium">{transform.statusText}</span>
          <button
            onClick={transform.cancelTransform}
            className="px-3 py-1 bg-white/10 hover:bg-red-500/50 rounded text-sm"
          >
            Abbrechen (Esc)
          </button>
        </div>
      )}
    </div>
  );
}

// Separate Komponente für Canvas-Inhalt (muss innerhalb von Canvas sein)
function SceneContent({ state, tools, interaction, cad, snap, displaySettings, setIsDragging, pointSettings, underlay, onUnderlayDrag, measurePoints, onAddMeasurePoint, componentTypes, selectedSolidId, setSelectedSolidId, hoveredSolidId, setHoveredSolidId, transform, geometryState, drawingTools, hoveredControlPoint, setHoveredControlPoint, geoSnap, wallState }) {
  const { camera, gl, raycaster, pointer } = useThree();

  // Refs für THREE-Objekte (werden nur einmal erstellt, nicht bei jedem Frame)
  const planeRef = useRef(new THREE.Plane(new THREE.Vector3(0, 0, 1), 0));
  const intersectionRef = useRef(new THREE.Vector3());

  // Mouse-Position tracken - aktualisiert nur Ref in useCADSettings (kein React State)
  useFrame(() => {
    raycaster.setFromCamera(pointer, camera);
    if (raycaster.ray.intersectPlane(planeRef.current, intersectionRef.current)) {
      const mouseX = Math.round(intersectionRef.current.x);
      const mouseY = Math.round(intersectionRef.current.y);

      cad.setMousePosition({ x: mouseX, y: mouseY });

      // Transform-Preview während Mausbewegung aktualisieren
      if (transform?.isSelectingTarget) {
        const snapped = snap.getSnappedPosition(mouseX, mouseY, state.points, state.walls, geoSnap);
        transform.updatePreview(snapped.x, snapped.y);
      }

      // Drawing tool preview während Mausbewegung aktualisieren
      if (drawingTools?.isWaitingForSecondPoint) {
        const snapped = snap.getSnappedPosition(mouseX, mouseY, state.points, state.walls, geoSnap);
        drawingTools.updateMousePosition(snapped.x, snapped.y);
      }
    }
  });

  // Klick-Handler für verschiedene Tools
  const handleBackgroundClick = useCallback((event) => {
    if (!event.point) return;

    // Modifier-Tasten prüfen
    const altKey = event.altKey || event.nativeEvent?.altKey || false;

    // Snapping mit CAD-Einstellungen
    const snapped = snap.getSnappedPosition(
      event.point.x,
      event.point.y,
      state.points,
      state.walls,
      geoSnap
    );

    // Transform-Modus: Basispunkt oder Zielpunkt setzen
    if (transform?.isActive) {
      if (transform.isSelectingBase) {
        transform.setBase(snapped.x, snapped.y);
        return;
      } else if (transform.isSelectingTarget) {
        transform.applyTransform();
        return;
      }
    }

    if (tools.activeTool === TOOLS.POINT) {
      const existing = state.findNearestPoint(snapped.x, snapped.y);
      if (!existing) {
        // Z-Höhe aus Punkt-Einstellungen verwenden
        const zHeight = 0; // Rhino-style: Points at Z=0 in plan view

        // Wandtrennend aus Einstellungen (Alt-Taste überschreibt)
        const shouldSplitWall = altKey ? false : (pointSettings?.wallSplitting ?? true);

        // Prüfen ob auf einer Wand geklickt wurde → Wall Splitting
        const wallHit = state.findWallAtPoint(snapped.x, snapped.y, SIZES.snapThreshold * 2);

        if (wallHit && shouldSplitWall) {
          // Punkt auf projizierter Position erstellen mit eingestellter Z-Höhe
          const newPoint = state.addPoint(wallHit.projectedPoint.x, wallHit.projectedPoint.y, zHeight);
          // Wand an diesem Punkt teilen
          state.splitWallAtPoint(wallHit.wall.id, newPoint.id);
          cad.setLastPoint({ x: wallHit.projectedPoint.x, y: wallHit.projectedPoint.y });
        } else {
          // Normaler Punkt mit eingestellter Z-Höhe
          state.addPoint(snapped.x, snapped.y, zHeight);
          cad.setLastPoint({ x: snapped.x, y: snapped.y });
        }
      }
    } else if (tools.activeTool === TOOLS.MEASURE) {
      // Messwerkzeug: Punkt zur Messung hinzufügen
      onAddMeasurePoint?.(snapped.x, snapped.y);
    }
    // ============ PURE GEOMETRY TOOLS (Rhino-like with preview) ============
    else if (tools.activeTool === TOOLS.LINE) {
      // Line-Tool: 2 clicks für gerade Linie
      if (!drawingTools.isActive) {
        // Erster Klick: Startpunkt merken
        drawingTools.startDrawing(snapped.x, snapped.y);
        cad.setLastPoint({ x: snapped.x, y: snapped.y });
      } else {
        // Zweiter Klick: Linie erstellen
        const result = drawingTools.confirmWithMouse();
        if (result) {
          geometryState.addLineWithPoints(result.firstPoint, result.secondPoint);
        }
        cad.setLastPoint({ x: snapped.x, y: snapped.y });
      }
    } else if (tools.activeTool === TOOLS.POLYLINE) {
      // Polyline-Tool: Multi-click, Enter to finish (handled by keyboard)
      cad.setLastPoint({ x: snapped.x, y: snapped.y });
      tools.addPendingPoint(`pl_${snapped.x}_${snapped.y}`);
    } else if (tools.activeTool === TOOLS.CIRCLE) {
      // Circle-Tool: Center + Radius mit Preview
      if (!drawingTools.isActive) {
        // Erster Klick: Center
        drawingTools.startDrawing(snapped.x, snapped.y);
        cad.setLastPoint({ x: snapped.x, y: snapped.y });
      } else {
        // Zweiter Klick: Radius-Punkt
        const result = drawingTools.confirmWithMouse();
        if (result && result.preview) {
          geometryState.addCircleWithPoints(result.preview.center, result.preview.radius);
        }
        tools.selectTool(TOOLS.SELECT);
      }
    } else if (tools.activeTool === TOOLS.RECTANGLE) {
      // Rectangle-Tool: 2 Ecken mit Preview
      if (!drawingTools.isActive) {
        // Erster Klick: Erste Ecke
        drawingTools.startDrawing(snapped.x, snapped.y);
        cad.setLastPoint({ x: snapped.x, y: snapped.y });
      } else {
        // Zweiter Klick: Gegenüberliegende Ecke
        const result = drawingTools.confirmWithMouse();
        if (result) {
          geometryState.addRectangle(result.firstPoint, result.secondPoint);
        }
        tools.selectTool(TOOLS.SELECT);
      }
    } else if (tools.activeTool === TOOLS.POLYGON) {
      // Polygon-Tool: Center + Radius mit Preview (6-Eck Standard)
      if (!drawingTools.isActive) {
        // Erster Klick: Center
        drawingTools.startDrawing(snapped.x, snapped.y);
        cad.setLastPoint({ x: snapped.x, y: snapped.y });
      } else {
        // Zweiter Klick: Vertex-Punkt (Radius + Rotation)
        const result = drawingTools.confirmWithMouse();
        if (result && result.preview) {
          geometryState.addPolygon(
            result.preview.center,
            result.preview.radius,
            result.preview.sides,
            result.preview.rotation
          );
        }
        tools.selectTool(TOOLS.SELECT);
      }
    }
    // ============ SOLID CREATION TOOLS with Preview ============
    else if (tools.activeTool === TOOLS.CYLINDER) {
      // Cylinder-Tool: Center + Radius mit Preview
      if (!drawingTools.isActive) {
        // Erster Klick: Basispunkt (Center)
        drawingTools.startDrawing(snapped.x, snapped.y);
        cad.setLastPoint({ x: snapped.x, y: snapped.y });
      } else {
        // Zweiter Klick: Radius-Punkt
        const result = drawingTools.confirmWithMouse();
        if (result && result.preview) {
          geometryState.addCylinder(
            { x: result.preview.center.x, y: result.preview.center.y, z: 0 },
            result.preview.radius,
            result.preview.height
          );
        }
        tools.selectTool(TOOLS.SELECT);
      }
    } else if (tools.activeTool === TOOLS.SPHERE) {
      // Sphere-Tool: Center + Radius mit Preview
      if (!drawingTools.isActive) {
        // Erster Klick: Center
        drawingTools.startDrawing(snapped.x, snapped.y);
        cad.setLastPoint({ x: snapped.x, y: snapped.y });
      } else {
        // Zweiter Klick: Radius-Punkt
        const result = drawingTools.confirmWithMouse();
        if (result && result.preview) {
          geometryState.addSphere(result.preview.center, result.preview.radius);
        }
        tools.selectTool(TOOLS.SELECT);
      }
    }
    // ============ CONSTRUCTION/GUIDE LINE TOOL ============
    else if (tools.activeTool === TOOLS.CONSTRUCTION_LINE) {
      // Hilfslinie: 2 Punkte für Richtung mit Preview
      if (!drawingTools.isActive) {
        // Erster Klick: Startpunkt
        drawingTools.startDrawing(snapped.x, snapped.y);
        cad.setLastPoint({ x: snapped.x, y: snapped.y });
      } else {
        // Zweiter Klick: Richtungspunkt
        const result = drawingTools.confirmWithMouse();
        if (result) {
          geometryState.addConstructionLine(result.firstPoint, result.secondPoint);
        }
        cad.setLastPoint({ x: snapped.x, y: snapped.y });
        // Bleibt im Construction-Tool für schnelles Mehrfach-Zeichnen
      }
    }
    // ============ BOX TOOL with Preview ============
    else if (tools.activeTool === TOOLS.BOX) {
      // Box-Tool: 2 Ecken mit Preview
      if (!drawingTools.isActive) {
        // Erster Klick: Erste Ecke
        drawingTools.startDrawing(snapped.x, snapped.y);
        cad.setLastPoint({ x: snapped.x, y: snapped.y });
      } else {
        // Zweiter Klick: Gegenüberliegende Ecke
        const result = drawingTools.confirmWithMouse();
        if (result && result.preview) {
          const c1 = result.preview.corner1;
          const c2 = result.preview.corner2;
          const width = Math.abs(c2.x - c1.x);
          const depth = Math.abs(c2.y - c1.y);
          const centerX = (c1.x + c2.x) / 2;
          const centerY = (c1.y + c2.y) / 2;
          geometryState.addBox(
            { x: centerX, y: centerY, z: result.preview.height / 2 },
            width,
            depth,
            result.preview.height
          );
        }
        tools.selectTool(TOOLS.SELECT);
      }
    }
  }, [tools.activeTool, tools.selectTool, state, cad, snap, pointSettings, onAddMeasurePoint, transform, geometryState, tools, drawingTools, geoSnap]);

  // Punkt-Klick Handler
  const handlePointClick = useCallback((pointId, event) => {
    const point = state.getPoint(pointId);
    const ctrlKey = event?.ctrlKey || event?.metaKey || false;
    const shiftKey = event?.shiftKey || false;

    // Ctrl+Click = Alles abwählen
    if (ctrlKey) {
      interaction.clearSelection();
      return;
    }

    // Transform-Modus: Punkt als Basispunkt oder Zielpunkt verwenden
    if (transform?.isActive && point) {
      if (transform.isSelectingBase) {
        transform.setBase(point.x, point.y);
        return;
      } else if (transform.isSelectingTarget) {
        transform.applyTransform();
        return;
      }
    }

    if (tools.activeTool === TOOLS.SELECT) {
      interaction.selectPoint(pointId, shiftKey);
    } else if (tools.activeTool === TOOLS.POINT) {
      // Im Punkt-Modus: Bestehenden Punkt als Referenz setzen
      // Danach kann der Benutzer eine Distanz eingeben
      if (point) {
        cad.setLastPoint({ x: point.x, y: point.y });
      }
    } else if (tools.activeTool === TOOLS.SPACE) {
      const pending = tools.pendingPointIds;

      if (pending.length >= 3 && pending[0] === pointId) {
        state.addSpace(pending);
        tools.resetPending();
        tools.selectTool(TOOLS.SELECT);
      } else if (!pending.includes(pointId)) {
        tools.addPendingPoint(pointId);
      }

      if (point) cad.setLastPoint({ x: point.x, y: point.y });
    } else if (tools.activeTool === TOOLS.ARC) {
      // Bogen-Tool: 3 Punkte sammeln → Geometrie-Arc erstellen
      const pending = tools.pendingPointIds;
      if (pending.length < 2) {
        if (!pending.includes(pointId)) {
          tools.addPendingPoint(pointId);
        }
        if (point) cad.setLastPoint({ x: point.x, y: point.y });
      } else if (pending.length === 2 && !pending.includes(pointId)) {
        // Dritter Punkt: Arc als Geometrie-Kurve erstellen
        const p1 = state.getPoint(pending[0]);
        const p2 = state.getPoint(pending[1]);
        const p3 = point;

        if (p1 && p2 && p3) {
          geometryState.addArc3PointWithPoints(
            { x: p1.x, y: p1.y, z: 0 },
            { x: p2.x, y: p2.y, z: 0 },
            { x: p3.x, y: p3.y, z: 0 }
          );
        }

        tools.resetPending();
        if (point) cad.setLastPoint({ x: point.x, y: point.y });
      }
    } else if (tools.activeTool === TOOLS.MEASURE) {
      // Messwerkzeug: Bestehenden Punkt zur Messung hinzufügen
      if (point) {
        onAddMeasurePoint?.(point.x, point.y);
      }
    }
  }, [tools, interaction, state, cad, pointSettings, onAddMeasurePoint, transform, geometryState]);

  // Punkt-Drag Handler
  // WICHTIG: Während des Drags nur Position aktualisieren, keine Arc-Neuberechnung!
  const handlePointDrag = useCallback((pointId, x, y) => {
    const snapped = snap.getSnappedPosition(x, y, state.points, state.walls, geoSnap);
    // Immer nur den Punkt verschieben - Arc wird bei DragEnd neu berechnet
    state.movePoint(pointId, snapped.x, snapped.y);
  }, [state, snap, geoSnap]);

  // Wand-Drag Handler (verschiebt beide Punkte parallel)
  const handleWallDrag = useCallback((wallId, offsetX, offsetY) => {
    const wall = state.walls.find(w => w.id === wallId);
    if (!wall) return;

    const startPoint = state.getPoint(wall.startPointId);
    const endPoint = state.getPoint(wall.endPointId);
    if (!startPoint || !endPoint) return;

    // Beide Punkte um den Offset verschieben
    state.movePoint(wall.startPointId, startPoint.x + offsetX, startPoint.y + offsetY);
    state.movePoint(wall.endPointId, endPoint.x + offsetX, endPoint.y + offsetY);
  }, [state]);

  // Wand-Split Handler (Punkt auf Wand setzen und teilen)
  const handleSplitWall = useCallback((wallId, clickPos) => {
    const wall = state.walls.find(w => w.id === wallId);
    if (!wall) return;

    const start = state.getPoint(wall.startPointId);
    const end = state.getPoint(wall.endPointId);
    if (!start || !end) return;

    // Punkt auf die Wand projizieren
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0) return;

    const t = Math.max(0.05, Math.min(0.95,
      ((clickPos.x - start.x) * dx + (clickPos.y - start.y) * dy) / len2
    ));

    const projX = start.x + t * dx;
    const projY = start.y + t * dy;

    // Neuen Punkt erstellen und Wand teilen
    const newPoint = state.addPoint(projX, projY);
    state.splitWallAtPoint(wallId, newPoint.id);
    cad.setLastPoint({ x: projX, y: projY });
  }, [state, cad]);

  // Control Point Drag Handlers
  const handleControlPointDragStart = useCallback((objectId, pointIndex, objectType, pointType) => {
    setIsDragging(true);
  }, [setIsDragging]);

  const handleControlPointDrag = useCallback((objectId, pointIndex, newPosition, objectType, pointType) => {
    // Apply snapping
    const snapped = snap.getSnappedPosition(newPosition.x, newPosition.y, state.points, state.walls, geoSnap);
    const snappedPos = { x: snapped.x, y: snapped.y, z: newPosition.z || 0 };

    if (objectType === 'curve') {
      if (pointType === 'center') {
        // Center point moves the entire curve
        geometryState.moveCurve(objectId, snappedPos);
      } else {
        geometryState.updateCurveControlPoint(objectId, pointIndex, snappedPos);
      }
    } else if (objectType === 'solid') {
      if (pointType === 'center') {
        // Center point moves the entire solid
        geometryState.moveSolid(objectId, snappedPos);
      } else {
        geometryState.updateSolidControlPoint(objectId, pointIndex, snappedPos);
      }
    }
  }, [snap, state.points, state.walls, geometryState, geoSnap]);

  const handleControlPointDragEnd = useCallback((objectId, pointIndex, newPosition, objectType, pointType) => {
    setIsDragging(false);
    // Final position with snapping
    const snapped = snap.getSnappedPosition(newPosition.x, newPosition.y, state.points, state.walls, geoSnap);
    const snappedPos = { x: snapped.x, y: snapped.y, z: newPosition.z || 0 };

    if (objectType === 'curve') {
      if (pointType === 'center') {
        geometryState.moveCurve(objectId, snappedPos);
      } else {
        geometryState.updateCurveControlPoint(objectId, pointIndex, snappedPos);
      }
    } else if (objectType === 'solid') {
      if (pointType === 'center') {
        geometryState.moveSolid(objectId, snappedPos);
      } else {
        geometryState.updateSolidControlPoint(objectId, pointIndex, snappedPos);
      }
    }
  }, [setIsDragging, snap, state.points, state.walls, geometryState, geoSnap]);

  return (
    <>
      {/* PDF Underlay - hinter allem anderen */}
      {underlay?.imageUrl && underlay?.visible && (
        <ImageUnderlay3D
          imageUrl={underlay.imageUrl}
          position={underlay.position || { x: 0, y: 0 }}
          scale={underlay.scale || 1}
          rotation={underlay.rotation || 0}
          opacity={underlay.opacity || 0.5}
          visible={underlay.visible}
          draggable={tools.activeTool === TOOLS.SELECT}
          locked={underlay.locked || false}
          onDrag={onUnderlayDrag}
        />
      )}

      {/* Unsichtbare Klick-Fläche für alle Zeichenwerkzeuge */}
      {(tools.activeTool === TOOLS.POINT || tools.activeTool === TOOLS.MEASURE || tools.activeTool === TOOLS.BOX || tools.activeTool === TOOLS.LINE || tools.activeTool === TOOLS.POLYLINE || tools.activeTool === TOOLS.CIRCLE || tools.activeTool === TOOLS.RECTANGLE || tools.activeTool === TOOLS.POLYGON || tools.activeTool === TOOLS.CYLINDER || tools.activeTool === TOOLS.SPHERE || tools.activeTool === TOOLS.CONSTRUCTION_LINE || transform?.isActive) && (
        <mesh
          position={[0, 0, -100]}
          onClick={handleBackgroundClick}
        >
          <planeGeometry args={[200000, 200000]} />
          <meshBasicMaterial transparent opacity={0} />
        </mesh>
      )}

      {/* Räume */}
      {displaySettings.showSpaceFill && state.spaces.map(space => (
        <Space3D
          key={space.id}
          space={space}
          points={state.points}
          isSelected={interaction.selectedSpaceIds?.includes(space.id) || interaction.selectedSpaceId === space.id}
          onSelect={(spaceId, event) => {
            const ctrlKey = event?.ctrlKey || event?.metaKey || false;
            if (ctrlKey) {
              interaction.clearSelection();
              return;
            }
            const shiftKey = event?.shiftKey || false;
            interaction.selectSpace(spaceId, shiftKey);
          }}
          showLabel={displaySettings.showLabels}
          labelSize={displaySettings.labelSize}
          fillOpacity={displaySettings.spaceFillOpacity}
          activeTool={tools.activeTool}
        />
      ))}

      {/* Wände (neues 8-Punkt-Volumenkörper-System) */}

      {/* New Wall Volume Bodies (8-point model from useWallState) */}
      {wallState.walls.map(wall => (
        <WallVolume3D
          key={wall.id}
          wall={wall}
          isSelected={interaction.selectedWallIds?.includes(wall.id)}
          isHovered={interaction.hoveredWallId === wall.id}
          viewMode={displaySettings.viewMode}
          showDimensions={displaySettings.showDimensions}
          dimensionFontSize={displaySettings.dimensionFontSize}
          onSelect={(wallId, event) => {
            const shiftKey = event?.shiftKey || false;
            interaction.selectWall(wallId, shiftKey);
          }}
          onHover={interaction.setHover}
        />
      ))}

      {/* Vorwände/Schächte: entfernt — jetzt als Geometrie + Shift+W */}

      {/* 3D-Körper (Solids) */}
      {state.solids && state.solids.map(solid => (
        <Solid3D
          key={solid.id}
          solid={solid}
          isSelected={selectedSolidId === solid.id}
          isHovered={hoveredSolidId === solid.id}
          onSelect={(solidId) => setSelectedSolidId(solidId)}
          onHover={(type, id) => {
            if (type === 'solid') setHoveredSolidId(id);
            else setHoveredSolidId(null);
          }}
          viewMode={displaySettings.viewMode}
          showAxes={true}
          showDimensions={displaySettings.showDimensions}
        />
      ))}

      {/* Pure Geometry: Solids (Cylinders, Spheres, Extrusions) */}
      {geometryState?.solids && geometryState.solids.length > 0 && geometryState.solids.map(solid => (
        <Solid3D
          key={solid.id}
          solid={solid}
          isSelected={selectedSolidId === solid.id}
          isHovered={hoveredSolidId === solid.id}
          onSelect={(solidId) => setSelectedSolidId(solidId)}
          onHover={(type, id) => {
            if (type === 'solid') setHoveredSolidId(id);
            else setHoveredSolidId(null);
          }}
          viewMode={displaySettings.viewMode}
          showAxes={true}
          showDimensions={displaySettings.showDimensions}
        />
      ))}

      {/* Pure Geometry: Curves (Lines, Polylines, Arcs, Circles) */}
      {geometryState?.curves && geometryState.curves.length > 0 && (
        <CurveObjects
          curves={geometryState.curves}
          selectedIds={interaction.selectedGeometryCurveIds || []}
          hoveredId={interaction.hoveredGeometryCurveId}
          onCurveClick={(id, e) => {
            const shiftKey = e?.shiftKey || false;
            if (tools.activeTool === TOOLS.SELECT) {
              interaction.selectGeometryCurve(id, shiftKey);
            }
          }}
          onCurveHover={(id) => interaction.setHover('geometryCurve', id)}
        />
      )}

      {/* Pure Geometry: Points (including control points for rectangles etc.) */}
      {geometryState?.points && geometryState.points.length > 0 && (
        <GeometryPoints
          points={geometryState.points}
          selectedIds={interaction.selectedGeometryPointIds || []}
          hoveredId={interaction.hoveredGeometryPointId}
          onPointClick={(id, e) => {
            const shiftKey = e?.shiftKey || false;
            if (tools.activeTool === TOOLS.SELECT) {
              interaction.selectGeometryPoint(id, shiftKey);
            }
          }}
          onPointHover={(id) => interaction.setHover('geometryPoint', id)}
          onPointDragStart={(id) => {
            setIsDragging(true);
          }}
          onPointDrag={(id, x, y) => {
            // Apply snapping during drag
            const snapped = snap.getSnappedPosition(x, y, state.points, state.walls, geoSnap);
            geometryState.movePoint(id, snapped.x, snapped.y);
          }}
          onPointDragEnd={(id, x, y) => {
            setIsDragging(false);
            // Final position with snapping
            const snapped = snap.getSnappedPosition(x, y, state.points, state.walls, geoSnap);
            geometryState.movePoint(id, snapped.x, snapped.y);
          }}
          draggable={tools.activeTool === TOOLS.SELECT}
          pointRadius={displaySettings.pointRadius}
        />
      )}

      {/* Drawing Tool Preview (Rhino-style) */}
      {drawingTools?.isWaitingForSecondPoint && drawingTools.preview && (
        <DrawingPreview
          preview={drawingTools.preview}
          firstPoint={drawingTools.firstPoint}
          currentPoint={drawingTools.currentMousePos}
          dimension={drawingTools.currentDimension}
          angle={drawingTools.currentAngle}
        />
      )}

      {/* Punkte */}
      {state.points.map(point => (
        <Point3D
          key={point.id}
          point={point}
          isSelected={interaction.selectedPointIds?.includes(point.id) || interaction.selectedPointId === point.id}
          isHovered={interaction.hoveredPointId === point.id}
          isPending={tools.pendingPointIds.includes(point.id)}
          onSelect={handlePointClick}
          onHover={interaction.setHover}
          onDragStart={() => setIsDragging(true)}
          onDrag={handlePointDrag}
          onDragEnd={(pointId, finalX, finalY) => {
            setIsDragging(false);

            // Position snappen
            const snapped = (finalX !== undefined && finalY !== undefined)
              ? snap.getSnappedPosition(finalX, finalY, state.points, state.walls, geoSnap)
              : null;
            const dragX = snapped?.x ?? state.getPoint(pointId)?.x;
            const dragY = snapped?.y ?? state.getPoint(pointId)?.y;
            if (dragX === undefined || dragY === undefined) return;

            // Prüfen ob der Punkt ein Arc-Through-Point ist → Arc neu berechnen
            const arcThrough = state.getArcForThroughPoint(pointId);
            if (arcThrough) {
              state.updateArc(arcThrough.id, dragX, dragY);
              return;
            }

            // Prüfen ob der Punkt ein Arc-Start-Punkt ist → Arc neu berechnen
            const arcStart = state.getArcForStartPoint(pointId);
            if (arcStart) {
              state.updateArcStart(arcStart.id, dragX, dragY);
              return;
            }

            // Prüfen ob der Punkt ein Arc-End-Punkt ist → Arc neu berechnen
            const arcEnd = state.getArcForEndPoint(pointId);
            if (arcEnd) {
              state.updateArcEnd(arcEnd.id, dragX, dragY);
              return;
            }

            // Prüfen ob der Punkt auf einen anderen gezogen wurde → Punkte verschmelzen
            const nearbyPoint = state.points.find(p => {
              if (p.id === pointId) return false;
              const dx = p.x - dragX;
              const dy = p.y - dragY;
              return Math.sqrt(dx * dx + dy * dy) < SIZES.snapThreshold;
            });
            if (nearbyPoint) {
              state.mergePoints(pointId, nearbyPoint.id);
              return;
            }

            state.saveSnapshot();
          }}
          draggable={tools.activeTool === TOOLS.SELECT}
          radius={displaySettings.pointRadius}
        />
      ))}

      {/* Pending-Linien (für Space/Wall Tool) */}
      {tools.pendingPointIds.length > 0 && (
        <PendingLines
          pointIds={tools.pendingPointIds}
          points={state.points}
          mousePosition={cad.mousePosition}
          orthoMode={cad.orthoMode}
        />
      )}

      {/* Tracking-Linie vom letzten Punkt (Rhino-Style Rubber Band) */}
      <TrackingLine
        lastPoint={cad.lastPoint}
        mousePosition={cad.mousePosition}
        orthoMode={cad.orthoMode}
        activeTool={tools.activeTool}
      />

      {/* Cursor-Annotation (Distanz/Winkel neben Maus) */}
      <CursorAnnotation
        lastPoint={cad.lastPoint}
        mousePosition={cad.mousePosition}
        orthoMode={cad.orthoMode}
        activeTool={tools.activeTool}
      />

      {/* Snap-Indikator */}
      <SnapIndicator snap={snap.activeSnap} />

      {/* Deckenansicht (nur in 3D) */}
      <CeilingView3D
        points={state.points}
        walls={state.walls}
        spaces={state.spaces}
        viewMode={displaySettings.viewMode}
        showLots={displaySettings.showLots}
        showCeilingPoints={displaySettings.showCeilingPoints}
        showCeilingLines={displaySettings.showCeilingLines}
        showCeilingFill={displaySettings.showCeilingFill}
        showHeightLabels={displaySettings.showHeightLabels}
        labelSize={displaySettings.labelSize / 3}
      />

      {/* Bogen-Mittelpunkte anzeigen */}
      <ArcCenters
        arcs={state.arcs}
        points={state.points}
      />

      {/* Bogen-Vorschau */}
      <ArcPreview
        pendingPointIds={tools.pendingPointIds}
        points={state.points}
        mousePosition={cad.mousePosition}
        activeTool={tools.activeTool}
      />

      {/* Mess-Overlay (Punkte, Linien, Fläche) */}
      {tools.activeTool === TOOLS.MEASURE && measurePoints && measurePoints.length > 0 && (
        <MeasureOverlay3D
          measurePoints={measurePoints}
          mousePosition={cad.mousePosition}
          showArea={measurePoints.length >= 3}
        />
      )}

      {/* Transform-Vorschau (Move/Copy Ghost-Objekte) */}
      {transform?.isActive && transform.previewData && (
        <TransformPreview
          previewData={transform.previewData}
          transformType={transform.transformType}
        />
      )}

      {/* Control Points für ausgewählte Geometrie (Rhino-style Grips) */}
      {tools.activeTool === TOOLS.SELECT && (
        <ControlPoints
          selectedCurves={interaction.selectedGeometryCurveIds || []}
          selectedSolids={interaction.selectedGeometrySolidIds || []}
          curves={geometryState?.curves || []}
          solids={geometryState?.solids || []}
          hoveredControlPoint={hoveredControlPoint}
          onControlPointDragStart={handleControlPointDragStart}
          onControlPointDrag={handleControlPointDrag}
          onControlPointDragEnd={handleControlPointDragEnd}
          onControlPointHover={setHoveredControlPoint}
          viewMode={displaySettings.viewMode}
        />
      )}
    </>
  );
}
